import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/App";
import { CRMLayout } from "@/components/CRMLayout";
import {
  ArrowLeft,
  Calendar,
  DollarSign,
  User,
  Bot,
  Send,
  Loader2,
  CheckCircle2,
  Clock,
  Edit3,
  Trash2,
  Save,
  FileText,
  Download,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import ProjectTabs from "../components/ProjectTabs";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const PROJECT_STAGES = [
  { value: "discovery", label: "Découverte", icon: "🔍" },
  { value: "design", label: "Design", icon: "🎨" },
  { value: "development", label: "Développement", icon: "💻" },
  { value: "testing", label: "Tests", icon: "🧪" },
  { value: "deployment", label: "Déploiement", icon: "🚀" },
  { value: "maintenance", label: "Maintenance", icon: "🔧" },
];

export default function ProjectDetailPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [project, setProject] = useState(null);
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});

  const downloadDocument = async (document) => {
    try {
      const response = await fetch(
        `${API_URL}/api/projects/${projectId}/documents/${document.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) throw new Error("Téléchargement impossible");
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = window.document.createElement("a");
      link.href = url;
      link.download = document.name;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error.message || "Document indisponible");
    }
  };

  // AI Chat
  const [aiMessages, setAiMessages] = useState([]);
  const [aiInput, setAiInput] = useState("");
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => {
    fetchProjectData();
  }, [projectId]);

  const fetchProjectData = async () => {
    try {
      const projectRes = await fetch(`${API_URL}/api/projects/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (projectRes.ok) {
        const projectData = await projectRes.json();
        setProject(projectData);
        setEditData({
          name: projectData.name,
          status: projectData.status,
          current_stage: projectData.current_stage,
          description: projectData.description,
          notes: projectData.notes || "",
        });

        // Fetch client
        if (projectData.client_id) {
          const clientRes = await fetch(
            `${API_URL}/api/clients/${projectData.client_id}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (clientRes.ok) {
            const clientData = await clientRes.json();
            setClient(clientData);
          }
        }

        // Initialize AI with context
        setAiMessages([
          {
            role: "assistant",
            content: `Je suis prêt à vous aider avec le projet "${projectData.name}". Ce projet est actuellement en phase de ${getStageName(
              projectData.current_stage
            )}. Comment puis-je vous assister ?`,
            suggestions: getStageSuggestions(projectData.current_stage),
          },
        ]);
      } else {
        toast.error("Projet non trouvé");
        navigate("/dashboard");
      }
    } catch (error) {
      console.error("Error fetching project:", error);
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  const getStageName = (stage) => {
    const found = PROJECT_STAGES.find((s) => s.value === stage);
    return found ? found.label : stage;
  };

  const getStageSuggestions = (stage) => {
    const suggestions = {
      discovery: [
        "Quels sont les objectifs business du client ?",
        "Qui est la cible principale ?",
        "Quelles sont les contraintes techniques ?",
      ],
      design: [
        "Le client a-t-il une charte graphique ?",
        "Quelles sont les références visuelles ?",
        "Mobile-first ou Desktop-first ?",
      ],
      development: [
        "Quels sont les prochains sprints ?",
        "Y a-t-il des blocages techniques ?",
        "Comment gérer le versioning ?",
      ],
      testing: [
        "Quels sont les cas de test prioritaires ?",
        "Tests de performance requis ?",
        "Le client participe à la recette ?",
      ],
      deployment: [
        "Quel hébergement est prévu ?",
        "DNS et SSL sont-ils prêts ?",
        "Plan de monitoring en place ?",
      ],
      maintenance: [
        "Quelle fréquence de mises à jour ?",
        "Budget maintenance défini ?",
        "Évolutions futures planifiées ?",
      ],
    };
    return suggestions[stage] || suggestions.discovery;
  };

  const handleUpdate = async () => {
    try {
      const response = await fetch(`${API_URL}/api/projects/${projectId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(editData),
      });

      if (response.ok) {
        const updated = await response.json();
        setProject(updated);
        setEditing(false);
        toast.success("Projet mis à jour");
      } else {
        toast.error("Erreur lors de la mise à jour");
      }
    } catch (error) {
      toast.error("Erreur de connexion");
    }
  };

  const handleDelete = async () => {
    try {
      const response = await fetch(`${API_URL}/api/projects/${projectId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        toast.success("Projet supprimé");
        navigate("/dashboard");
      } else {
        toast.error("Erreur lors de la suppression");
      }
    } catch (error) {
      toast.error("Erreur de connexion");
    }
  };

  const sendAiMessage = async () => {
    if (!aiInput.trim()) return;

    const userMessage = { role: "user", content: aiInput };
    setAiMessages((prev) => [...prev, userMessage]);
    setAiInput("");
    setAiLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/ai/assistant`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          project_id: projectId,
          message: aiInput,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setAiMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: data.response,
            suggestions: data.suggestions,
          },
        ]);
      }
    } catch (error) {
      console.error("AI error:", error);
    } finally {
      setAiLoading(false);
    }
  };

  if (loading) {
    return (
      <CRMLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      </CRMLayout>
    );
  }

  if (!project) {
    return (
      <CRMLayout>
        <div className="p-8 text-center">
          <p className="text-slate-500">Projet non trouvé</p>
          <Link to="/dashboard">
            <Button className="mt-4">Retour au dashboard</Button>
          </Link>
        </div>
      </CRMLayout>
    );
  }

  const currentStageIndex = PROJECT_STAGES.findIndex(
    (s) => s.value === project.current_stage
  );

  return (
    <CRMLayout>
      <div className="p-6 lg:p-8" data-testid="project-detail-page">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-2 text-slate-500 hover:text-indigo-600 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour au dashboard
          </Link>

          <ProjectTabs projectId={projectId} actif="apercu" />

          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div>
              {editing ? (
                <Input
                  value={editData.name}
                  onChange={(e) =>
                    setEditData({ ...editData, name: e.target.value })
                  }
                  className="text-2xl font-bold mb-2"
                  data-testid="edit-name-input"
                />
              ) : (
                <h1
                  className="text-2xl font-bold text-slate-900 mb-2"
                  data-testid="project-title"
                >
                  {project.name}
                </h1>
              )}
              <div className="flex items-center gap-3">
                <Badge
                  className={
                    project.status === "active"
                      ? "bg-blue-500"
                      : project.status === "completed"
                      ? "bg-green-500"
                      : "bg-amber-500"
                  }
                >
                  {project.status === "active"
                    ? "En cours"
                    : project.status === "completed"
                    ? "Terminé"
                    : "En attente"}
                </Badge>
                <Badge variant="outline">
                  {project.project_type === "web"
                    ? "Web"
                    : project.project_type === "mobile"
                    ? "Mobile"
                    : "Web + Mobile"}
                </Badge>
              </div>
            </div>

            <div className="flex gap-2">
              {editing ? (
                <>
                  <Button
                    variant="outline"
                    onClick={() => setEditing(false)}
                    data-testid="cancel-edit-btn"
                  >
                    Annuler
                  </Button>
                  <Button
                    onClick={handleUpdate}
                    className="nova-btn-primary"
                    data-testid="save-edit-btn"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Enregistrer
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="outline"
                    onClick={() => setEditing(true)}
                    data-testid="edit-btn"
                  >
                    <Edit3 className="w-4 h-4 mr-2" />
                    Modifier
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="outline"
                        className="text-red-600 hover:bg-red-50"
                        data-testid="delete-btn"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Supprimer le projet ?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Cette action est irréversible. Le projet sera définitivement supprimé.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Annuler</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={handleDelete}
                          className="bg-red-600 hover:bg-red-700"
                        >
                          Supprimer
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <Card className="mb-8">
          <CardContent className="overflow-x-auto p-4 sm:p-6">
            <h3 className="font-semibold text-slate-900 mb-4">
              Progression du projet
            </h3>
            <div className="flex min-w-[520px] items-center justify-between">
              {PROJECT_STAGES.map((stage, index) => (
                <div
                  key={stage.value}
                  className="flex flex-col items-center relative flex-1"
                >
                  {index > 0 && (
                    <div
                      className={`absolute top-5 right-1/2 w-full h-1 ${
                        index <= currentStageIndex
                          ? "bg-indigo-600"
                          : "bg-slate-200"
                      }`}
                    />
                  )}
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center z-10 text-lg ${
                      index < currentStageIndex
                        ? "bg-indigo-600 text-white"
                        : index === currentStageIndex
                        ? "bg-indigo-600 text-white ring-4 ring-indigo-200"
                        : "bg-slate-200 text-slate-400"
                    }`}
                  >
                    {index < currentStageIndex ? (
                      <CheckCircle2 className="w-5 h-5" />
                    ) : (
                      stage.icon
                    )}
                  </div>
                  <span
                    className={`text-xs mt-2 font-medium ${
                      index <= currentStageIndex
                        ? "text-indigo-600"
                        : "text-slate-400"
                    }`}
                  >
                    {stage.label}
                  </span>
                </div>
              ))}
            </div>

            {editing && (
              <div className="mt-6 pt-4 border-t">
                <label className="text-sm font-medium text-slate-700 mb-2 block">
                  Changer l'étape actuelle
                </label>
                <Select
                  value={editData.current_stage}
                  onValueChange={(value) =>
                    setEditData({ ...editData, current_stage: value })
                  }
                >
                  <SelectTrigger className="w-64" data-testid="stage-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PROJECT_STAGES.map((stage) => (
                      <SelectItem key={stage.value} value={stage.value}>
                        {stage.icon} {stage.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Project Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Description */}
            <Card>
              <CardHeader>
                <CardTitle>Description</CardTitle>
              </CardHeader>
              <CardContent>
                {editing ? (
                  <Textarea
                    value={editData.description}
                    onChange={(e) =>
                      setEditData({ ...editData, description: e.target.value })
                    }
                    rows={4}
                    className="nova-input"
                    data-testid="edit-description"
                  />
                ) : (
                  <p className="text-slate-600">{project.description}</p>
                )}
              </CardContent>
            </Card>

            {/* Details Grid */}
            <div className="grid sm:grid-cols-2 gap-6">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                      <DollarSign className="w-5 h-5 text-indigo-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Budget</p>
                      <p className="font-semibold text-slate-900">
                        {project.budget_range}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                      <Clock className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Délai</p>
                      <p className="font-semibold text-slate-900">
                        {project.timeline}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Features */}
            {project.features && project.features.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Fonctionnalités</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {project.features.map((feature, index) => (
                      <Badge key={index} variant="secondary">
                        {feature}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Client Info */}
            {client && (
              <Card>
                <CardHeader>
                  <CardTitle>Client</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center">
                      <User className="w-6 h-6 text-slate-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">
                        {client.company_name}
                      </p>
                      <p className="text-sm text-slate-500">
                        {client.contact_name} • {client.email}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Project documents */}
            {project.documents && project.documents.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Documents de reprise</CardTitle>
                  <CardDescription>
                    Cahier des charges, guide développeur et état de reprise
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-2">
                  {project.documents.map((document) => (
                    <div key={document.id} className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 p-3">
                      <div className="flex min-w-0 items-center gap-3">
                        <FileText className="h-5 w-5 shrink-0 text-indigo-600" />
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-slate-900">{document.label || document.name}</p>
                          <p className="text-xs text-slate-500">{document.kind || "Document projet"}</p>
                        </div>
                      </div>
                      <Button variant="outline" size="sm" onClick={() => downloadDocument(document)}>
                        <Download className="mr-2 h-4 w-4" />
                        Télécharger
                      </Button>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Notes */}
            <Card>
              <CardHeader>
                <CardTitle>Notes</CardTitle>
                <CardDescription>
                  Notes internes sur le projet
                </CardDescription>
              </CardHeader>
              <CardContent>
                {editing ? (
                  <Textarea
                    value={editData.notes}
                    onChange={(e) =>
                      setEditData({ ...editData, notes: e.target.value })
                    }
                    rows={4}
                    placeholder="Ajoutez des notes sur le projet..."
                    className="nova-input"
                    data-testid="edit-notes"
                  />
                ) : (
                  <p className="text-slate-600">
                    {project.notes || "Aucune note pour le moment."}
                  </p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* AI Assistant */}
          <div className="lg:col-span-1">
            <Card className="h-[700px] flex flex-col sticky top-4">
              <CardHeader className="border-b">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
                    <Bot className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <CardTitle className="text-base">Assistant IA</CardTitle>
                    <CardDescription className="text-xs">
                      Aide à la gestion de projet
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>

              <ScrollArea className="flex-1 p-4">
                <div className="space-y-4">
                  {aiMessages.map((msg, index) => (
                    <div
                      key={index}
                      className={`flex ${
                        msg.role === "user" ? "justify-end" : "justify-start"
                      }`}
                    >
                      <div
                        className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                          msg.role === "user"
                            ? "bg-indigo-600 text-white"
                            : "bg-slate-100 text-slate-900"
                        }`}
                      >
                        <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                        {msg.suggestions && msg.suggestions.length > 0 && (
                          <div className="mt-3 space-y-2">
                            <p className="text-xs opacity-70">
                              Questions suggérées:
                            </p>
                            {msg.suggestions.map((suggestion, sIndex) => (
                              <button
                                key={sIndex}
                                onClick={() => setAiInput(suggestion)}
                                className="block w-full text-left text-xs bg-white/20 hover:bg-white/30 rounded-lg px-3 py-2 transition-colors"
                              >
                                {suggestion}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  {aiLoading && (
                    <div className="flex justify-start">
                      <div className="bg-slate-100 rounded-2xl px-4 py-3">
                        <Loader2 className="w-5 h-5 animate-spin text-indigo-600" />
                      </div>
                    </div>
                  )}
                </div>
              </ScrollArea>

              <div className="p-4 border-t">
                <div className="flex gap-2">
                  <Input
                    value={aiInput}
                    onChange={(e) => setAiInput(e.target.value)}
                    placeholder="Posez votre question..."
                    onKeyPress={(e) => e.key === "Enter" && sendAiMessage()}
                    className="nova-input"
                    data-testid="project-ai-input"
                  />
                  <Button
                    onClick={sendAiMessage}
                    disabled={aiLoading || !aiInput.trim()}
                    className="nova-btn-primary px-4"
                    data-testid="project-ai-send"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </CRMLayout>
  );
}
